import React, { useEffect, useState } from 'react';
import NetLabBackground from './NetLabBackground';
import UploadPanel from './netlab/UploadPanel';
import NetbankBrowser from './netlab/NetbankBrowser';
import NetLabEditor from './netlab/NetLabEditor';
import { netlabApi } from './netlab/api';
import './netlab/netlab.css';

const LOGO_URL = process.env.PUBLIC_URL + '/netlab_logo.svg';

// Keep the open session in the URL so a refresh returns to the same grid.
function setSessionParam(sid) {
  const params = new URLSearchParams(window.location.search);
  if (sid) params.set('session', sid); else params.delete('session');
  // A NetBank preselection (from NetGen's 3D view) is used once, then dropped.
  ['task', 'level', 'grid'].forEach(k => params.delete(k));
  window.history.replaceState(window.history.state, '', `?${params.toString()}`);
}

// Arriving from NetGen's 3D view: ?task=<id>[&level=lv|mv|merged&grid=<name>]
function readPreselect() {
  const params = new URLSearchParams(window.location.search);
  const task = params.get('task');
  if (!task || params.get('session')) return null;
  return { task, level: params.get('level'), grid: params.get('grid') };
}

export default function NetLabPage({ onLogout, user }) {
  const [preselect] = useState(readPreselect);
  const [dialog, setDialog] = useState(() => (preselect ? 'netbank' : null));   // 'upload' | 'netbank' | null
  const [loaded, setLoaded] = useState(null);   // { session, view }
  const [restoring, setRestoring] = useState(
    () => !!new URLSearchParams(window.location.search).get('session'));
  const [notice, setNotice] = useState('');

  useEffect(() => {
    const prev = document.title;
    document.title = 'NetLab';
    return () => { document.title = prev; };
  }, []);

  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get('session');
    if (!sid) return;
    netlabApi.session(sid)
      .then(setLoaded)
      .catch((e) => { setSessionParam(null); setNotice(e.message); })
      .finally(() => setRestoring(false));
  }, []);

  const open = (data) => {
    setDialog(null);
    setNotice('');
    setSessionParam(data.session.id);
    setLoaded(data);
  };

  if (loaded) {
    return (
      <NetLabEditor
        key={loaded.session.id}
        initial={loaded}
        onClose={() => { setSessionParam(null); setLoaded(null); }}
      />
    );
  }

  return (
    <div className="intro-container netlab-container">
      <NetLabBackground />
      <img src={LOGO_URL} alt="NetLab logo" className="intro-logo netlab-logo intro-title" />
      <div className="intro-options animated-options">
        <button className="intro-button netlab-button" onClick={() => setDialog('upload')} disabled={restoring}>
          Upload Network
        </button>
        <button className="intro-button netlab-button secondary" onClick={() => setDialog('netbank')} disabled={restoring}>
          Load from NetBank
        </button>
      </div>
      {(restoring || notice) && (
        <div className="nl-landing-notice">{restoring ? 'Reopening your grid…' : notice}</div>
      )}
      <div className="intro-footer animated-options">
        <a href={process.env.PUBLIC_URL + '/'} className="intro-link netlab-link">
          Back to NetGen
        </a>
        <a href="mailto:giourgkert.partalis@ait.ac.at" className="intro-link netlab-link">
          Contact Us
        </a>
        {onLogout && (
          <a
            href="#logout"
            className="intro-link netlab-link"
            title={`Logged in as ${user?.preferred_username || ''}`}
            onClick={(e) => { e.preventDefault(); onLogout(); }}
          >
            Log out
          </a>
        )}
      </div>
      {dialog === 'upload' && <UploadPanel onLoaded={open} onClose={() => setDialog(null)} />}
      {dialog === 'netbank' && <NetbankBrowser onLoaded={open} onClose={() => setDialog(null)} preselect={preselect} />}
    </div>
  );
}
